/**
 * The contacts register, usable standalone or nested under a client.
 *
 * One component for both because the difference is a parameter, not a screen.
 * `clientPublicId` narrows it and the table becomes the Contacts tab on a
 * client page; omitted, it is the organization-wide register.
 *
 * `queryKey` includes the client id. Without it the cache would serve one
 * client's contacts on another client's page — the classic nested-register bug,
 * and one that looks like data corruption rather than a caching mistake.
 */

import { Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { Mail, Phone, Plus, UserRound } from 'lucide-react';
import {
  ApiError,
  Button,
  Can,
  ServerTable,
  StatusChip,
  api,
  buttonClass,
  createFacetsFetcher,
  createServerFetcher,
  formatDate,
  toast,
  type ServerColumn,
  type Tone,
} from '@connect/web-shared';

import { ContactFormDialog } from './contact-form-dialog';

export interface ContactRow {
  public_id: string;
  client_public_id: string;
  client_name: string;
  given_name: string;
  surname: string;
  full_name: string;
  job_title: string | null;
  email: string | null;
  phone: string | null;
  role: string;
  is_active: boolean;
  created_at: string;
}

const ROLE_TONES: Record<string, Tone> = {
  primary: 'accent',
  billing: 'info',
  technical: 'neutral',
  other: 'neutral',
};

const ROLE_OPTIONS = [
  { value: 'primary', label: 'Primary' },
  { value: 'billing', label: 'Billing' },
  { value: 'technical', label: 'Technical' },
  { value: 'other', label: 'Other' },
];

const ENDPOINT = '/crm/contacts';

export function ContactsTable({
  clientPublicId,
  clientName,
}: {
  clientPublicId?: string;
  clientName?: string;
}) {
  const [formOpen, setFormOpen] = useState(false);
  const [recentKey, setRecentKey] = useState<string | null>(null);

  const fetchRows = useMemo(
    () => createServerFetcher<ContactRow>(ENDPOINT, { client: clientPublicId }),
    [clientPublicId],
  );
  const getFacets = useMemo(
    () => createFacetsFetcher(ENDPOINT, { client: clientPublicId }),
    [clientPublicId],
  );

  const columns = useMemo<ServerColumn<ContactRow>[]>(() => {
    const list: ServerColumn<ContactRow>[] = [
      {
        field: 'full_name',
        header: 'Name',
        icon: <UserRound className="size-3" />,
        filter: 'text',
        minWidth: 180,
        render: (row) => (
          <span className="flex flex-col">
            <span className="truncate font-medium">{row.full_name}</span>
            {row.job_title ? (
              <span className="truncate text-2xs text-faint">{row.job_title}</span>
            ) : null}
          </span>
        ),
      },
      {
        field: 'role',
        header: 'Role',
        filter: 'select',
        options: ROLE_OPTIONS,
        width: 110,
        render: (row) => <StatusChip value={row.role} tones={ROLE_TONES} />,
      },
      {
        field: 'email',
        header: 'Email',
        icon: <Mail className="size-3" />,
        filter: 'text',
        minWidth: 200,
        render: (row) =>
          row.email ? (
            <a href={`mailto:${row.email}`} className="truncate text-accent hover:underline">
              {row.email}
            </a>
          ) : (
            <span className="text-faint">—</span>
          ),
      },
      {
        field: 'phone',
        header: 'Phone',
        icon: <Phone className="size-3" />,
        filter: 'text',
        width: 150,
      },
      {
        field: 'is_active',
        header: 'Active',
        filter: 'boolean',
        align: 'center',
        width: 90,
        render: (row) =>
          row.is_active ? (
            <StatusChip value="active" tones={{ active: 'success' }} />
          ) : (
            <StatusChip value="inactive" tones={{ inactive: 'neutral' }} />
          ),
      },
      {
        field: 'created_at',
        header: 'Added',
        filter: 'date',
        width: 130,
        format: (value) => formatDate(value as string | null),
      },
    ];

    // The client column is redundant on a client's own page, where every row
    // has the same value. Inserted only for the standalone register.
    if (!clientPublicId) {
      list.splice(1, 0, {
        field: 'client_name',
        header: 'Client',
        filter: 'select',
        minWidth: 160,
      });
    }
    return list;
  }, [clientPublicId]);

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Can permission="crm.contact.create">
          {/* Adding a contact needs a client, and the standalone register does
              not know which. Rather than a dialog with a client picker nobody
              asked for, it points at the clients — where the action makes sense
              and the client is chosen. A link, not a button that can never be
              pressed with its reason in a tooltip nobody on a phone can read. */}
          {clientPublicId ? (
            <Button size="sm" onClick={() => setFormOpen(true)}>
              <Plus className="size-3.5" />
              New contact
            </Button>
          ) : (
            <Link to="/clients" className={buttonClass({ variant: 'secondary', size: 'sm' })}>
              <Plus className="size-3.5" />
              Add from a client's page
            </Link>
          )}
        </Can>
      </div>

      <ServerTable<ContactRow>
        columns={columns}
        fetchRows={fetchRows}
        getFacets={getFacets}
        rowKey={(row) => row.public_id}
        // The client id is part of the key — see the header.
        queryKey={['crm', 'contacts', clientPublicId ?? 'all']}
        stateKey={clientPublicId ? 'crm.contacts.nested' : 'crm.contacts'}
        searchPlaceholder="Search by name or email"
        viewField="full_name"
        recentKey={recentKey}
        fillHeight={!clientPublicId}
        onDelete={async (row) => {
          try {
            await api.delete(`${ENDPOINT}/${row.public_id}`);
            toast.success(`${row.full_name} removed`);
          } catch (error) {
            toast.error('Could not remove', error instanceof ApiError ? error.message : undefined);
            throw error;
          }
        }}
        deleteLabel={(row) => row.full_name}
        emptyTitle={clientName ? `No contacts at ${clientName}` : 'No contacts yet'}
        emptyDescription="A contact is a person at a client — who to email about an invoice, who signs off a project."
        emptyAction={
          clientPublicId ? (
            <Can permission="crm.contact.create">
              <Button size="sm" onClick={() => setFormOpen(true)}>
                <Plus className="size-3.5" />
                Add a contact
              </Button>
            </Can>
          ) : undefined
        }
      />

      {clientPublicId ? (
        <ContactFormDialog
          open={formOpen}
          clientPublicId={clientPublicId}
          onClose={() => setFormOpen(false)}
          onCreated={(publicId) => {
            setRecentKey(publicId);
            setFormOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}
